import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { Seo } from '@/components/site/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate('/login', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/my/estimates`, { withCredentials: true });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user) return null;

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-[70vh] bg-brand-bone" data-testid="page-account">
      <Seo title="My Account | HearthOak Construction" noindex path="/account" />
      <div className="container-x py-14 lg:py-20 max-w-3xl">
        <div className="flex items-start justify-between gap-4 border-b border-brand-border pb-6">
          <div>
            <div className="eyebrow mb-2">My account</div>
            <h1 className="h2">Hi, {user.name?.split(' ')[0] || 'there'}.</h1>
            <p className="font-sans text-sm text-brand-muted mt-2">{user.email}</p>
          </div>
          <button onClick={onLogout} className="btn-secondary" data-testid="account-logout">Sign out</button>
        </div>

        {user.role === 'admin' && (
          <div className="mt-6 border border-brand-border bg-brand-boneAlt p-4 flex items-center justify-between">
            <span className="font-sans text-sm text-brand-charcoal">You have admin access.</span>
            <Link to="/admin" className="btn-primary" data-testid="account-admin-link">Open admin</Link>
          </div>
        )}

        <div className="mt-10">
          <h2 className="font-serif text-2xl text-brand-charcoal">Your estimate requests</h2>
          {loading ? (
            <p className="mt-6 font-sans text-sm text-brand-muted">Loading…</p>
          ) : items.length === 0 ? (
            <div className="mt-6 border border-brand-border p-8 text-center" data-testid="account-empty">
              <Icons.ClipboardList size={28} strokeWidth={1.5} className="mx-auto text-brand-maroon" />
              <p className="mt-4 font-sans text-sm text-brand-muted">
                No requests yet under this email. When you request an estimate with this address, it will show up here.
              </p>
              <Link to="/estimate" className="btn-primary mt-6 inline-block" data-testid="account-estimate-cta">Request an Estimate</Link>
            </div>
          ) : (
            <ul className="mt-6 border-t border-brand-border" data-testid="account-estimates">
              {items.map((it) => (
                <li key={it.id} className="py-5 border-b border-brand-border" data-testid={`account-estimate-${it.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="font-serif text-lg text-brand-charcoal">{it.service_needed}</div>
                    <div className="font-sans text-xs uppercase tracking-wider text-brand-muted">{formatDate(it.created_at)}</div>
                  </div>
                  <p className="mt-1 font-sans text-sm text-brand-muted line-clamp-2">{it.project_description}</p>
                  <div className="mt-1 font-sans text-xs text-brand-muted">{it.address_or_city} · {it.preferred_timeline || 'timeline not set'}</div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-6 font-sans text-xs text-brand-muted">
            We review every request personally and follow up within 24 hours. Questions? Call <a href="tel:+18173843496" className="text-brand-maroon">817-384-FIXO</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
